/**
 * "קידום משרות" — job-promotion campaigns view.
 *
 * Shows the T.B campaigns whose name contains "קמפיין מועמדים": each ad set
 * promotes ONE job, its name carrying the JobSpace job number. Spend and
 * delivery come from the data store; candidate counts come live from the
 * JobSpace ATS for the selected range. KPIs: jobs count, candidates, spend,
 * cost per candidate — plus a per-job table.
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  Users,
  DollarSign,
  Target,
  Loader2,
  Search,
  AlertTriangle,
} from 'lucide-react';
import apiClient from '../api/client';
import { formatCurrency, formatNumber } from '../lib/utils';
import KPICard from '../components/dashboard/KPICard';
import DateRangeSelector from '../components/dashboard/DateRangeSelector';
import { useDateRangeStore } from '../store/dateRangeStore';

/** Why a row shows the number it shows — anything but 'ats' is a fallback. */
type CandidatesSource =
  | 'ats'
  | 'platform_no_job_number'
  | 'platform_not_in_ats'
  | 'platform_ats_unavailable';

interface JobPromotionRow {
  key: string;
  jobName: string;
  jobId: number | null;
  campaignName: string;
  channel: 'lead_form' | 'landing_page';
  spend: number;
  candidates: number;
  platformCandidates: number;
  candidatesSource: CandidatesSource;
  last24hCandidates: number;
  impressions: number;
  clicks: number;
  costPerCandidate: number | null;
  status?: string;
}

interface JobPromotionsResult {
  summary: {
    jobsCount: number;
    totalCandidates: number;
    totalSpend: number;
    avgCostPerCandidate: number | null;
    last24hCandidates: number;
    atsUnavailable: boolean;
  };
  jobs: JobPromotionRow[];
}

type StatusFilter = 'all' | 'active' | 'inactive';

async function getJobPromotions(
  startDate: string,
  endDate: string,
  status: StatusFilter
): Promise<JobPromotionsResult> {
  const response = await apiClient.get('/insights/job-promotions', {
    params: { dateRange: 'custom', startDate, endDate, status },
  });
  return response.data;
}

/**
 * The candidates cell: the ATS number for the selected range. When the ATS
 * can't answer for a row — no job number in the ad-set name, unknown job, or
 * the API being down — it shows the platform-measured count with a marker,
 * so a fallback is never mistaken for the real thing.
 */
function CandidatesCell({ job }: { job: JobPromotionRow }) {
  const { t } = useTranslation();

  const fallbackTitle =
    job.candidatesSource === 'platform_no_job_number'
      ? t('jobPromotions.noJobNumber')
      : job.candidatesSource === 'platform_not_in_ats'
        ? t('jobPromotions.notInAts', { jobId: job.jobId })
        : t('jobPromotions.atsDown');

  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className="font-medium">{formatNumber(job.candidates)}</span>
      {job.candidatesSource !== 'ats' && (
        <span title={fallbackTitle} className="text-amber-500">
          <AlertTriangle className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}

export default function JobPromotions() {
  const { t } = useTranslation();
  const { dateRange, setDateRange } = useDateRangeStore();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['job-promotions', dateRange.startDate, dateRange.endDate, statusFilter],
    queryFn: () => getJobPromotions(dateRange.startDate, dateRange.endDate, statusFilter),
    // Candidate counts are read live from the ATS, so the page refetches on
    // open rather than serving a half-hour-old snapshot like it used to.
    staleTime: 60 * 1000,
  });

  const summary = data?.summary;

  // Client-side job search — matches title or job number.
  const [search, setSearch] = useState('');
  const filteredJobs = useMemo(() => {
    const list = data?.jobs ?? [];
    const q = search.trim();
    if (!q) return list;
    return list.filter((j) => j.jobName.includes(q));
  }, [data, search]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('jobPromotions.title')}</h1>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* ATS degraded — the whole page is on platform numbers, say so once */}
      {summary?.atsUnavailable && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <p className="text-sm font-medium text-amber-900">
            {t('jobPromotions.atsUnavailable')}
          </p>
        </div>
      )}

      {/* Last-24h banner */}
      {summary && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <Users className="h-5 w-5 text-blue-600" />
          <p className="text-sm font-medium text-blue-900">
            {t('jobPromotions.last24h', { count: summary.last24hCandidates })}
          </p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title={t('jobPromotions.jobsCount')}
          value={summary ? formatNumber(summary.jobsCount) : '—'}
          icon={<Briefcase className="h-5 w-5" />}
          loading={isLoading}
        />
        <KPICard
          title={t('jobPromotions.candidates')}
          value={summary ? formatNumber(summary.totalCandidates) : '—'}
          icon={<Users className="h-5 w-5" />}
          loading={isLoading}
        />
        <KPICard
          title={t('kpis.spend')}
          value={summary ? formatCurrency(summary.totalSpend) : '—'}
          icon={<DollarSign className="h-5 w-5" />}
          loading={isLoading}
        />
        <KPICard
          title={t('jobPromotions.costPerCandidate')}
          value={
            summary?.avgCostPerCandidate != null
              ? formatCurrency(summary.avgCostPerCandidate)
              : '—'
          }
          icon={<Target className="h-5 w-5" />}
          loading={isLoading}
        />
      </div>

      {/* Per-job table */}
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b px-6 py-4">
          <h2 className="font-medium text-gray-900">{t('jobPromotions.tableTitle')}</h2>
          <div className="flex items-center gap-3">
            <div className="inline-flex overflow-hidden rounded-md border border-gray-300 text-sm">
              {(['all', 'active', 'inactive'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-1.5 ${
                    statusFilter === f
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t(`jobPromotions.filter_${f}`)}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('jobPromotions.searchPlaceholder')}
                className="w-64 rounded-md border border-gray-300 py-1.5 pe-3 ps-8 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-gray-500">
            {t('jobPromotions.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-start text-xs uppercase text-gray-500">
                  <th className="px-6 py-3 text-start">{t('jobPromotions.jobNumber')}</th>
                  <th className="px-4 py-3 text-end">{t('jobPromotions.candidates')}</th>
                  <th className="px-4 py-3 text-end">{t('jobPromotions.last24hShort')}</th>
                  <th className="px-4 py-3 text-end">{t('kpis.spend')}</th>
                  <th className="px-4 py-3 text-end">{t('jobPromotions.costPerCandidate')}</th>
                  <th className="px-4 py-3 text-center">{t('insights.status')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.key} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-6 py-3 font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <span dir="ltr">{job.jobName}</span>
                        <span
                          className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${
                            job.channel === 'landing_page'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {job.channel === 'landing_page'
                            ? t('jobPromotions.channelLanding')
                            : t('jobPromotions.channelLeadForm')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-end">
                      <CandidatesCell job={job} />
                    </td>
                    <td className="px-4 py-3 text-end">
                      {job.last24hCandidates > 0 ? (
                        <span className="inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          +{formatNumber(job.last24hCandidates)}
                        </span>
                      ) : (
                        <span className="text-gray-300">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">{formatCurrency(job.spend)}</td>
                    <td className="px-4 py-3 text-end">
                      {job.costPerCandidate != null ? formatCurrency(job.costPerCandidate) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                          job.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {job.status === 'ACTIVE'
                          ? t('insights.statusActive')
                          : t('insights.statusInactive')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
